import { Dayjs } from "dayjs";
import { CancellationProtectionPreference } from "./CancellationProtectionPreference";
import { ProductCatalog } from "./Catalog";
import { CheckInPreference } from "./CheckInPreference";
import { ExperienceOptionPreference } from "./ExperienceOption";
import { GuidePreference } from "./GuidePreference";
import { MeetingLocation } from "./Location";
import { Media } from "./Media";
import { NotificationPreferenceType } from "./NotificationPreference";
import { PaymentMethod } from "./Payment";
import { PrioritizedResource } from "./PrioritizedResource";
import { Product, ProductObject } from "./Product";
import { QrCodePreference } from "./QrCodePreference";
import { ReviewPreference } from "./ReviewPreference";
import { Schedule } from "./Schedule";
import { Term } from "./Term";
import { EquipmentAllocationStrategy, PrioritizedResourceID } from "./UserPreferences";
import { VirtualMeetingPreference } from "./VirtualMeeting";
import { WaiverExperiencePreference } from "./Waiver";
import { Weekday } from "./Weekday";

export interface Experience extends Product {
    object: ProductObject.EXPERIENCE;
    seller: {
        id: ID;
        name?: string;
    };
    allowedPrivacies: AllowedPrivacy[];
    resources: PrioritizedResource[];
    /* @deprecated Use isScheduled() or scheduleType instead */
    isScheduled?: boolean; // This is deprecated
    currency: string;
    scheduleType?: ScheduleType;
    schedules: Schedule[];
    optionsPreference?: ExperienceOptionPreference;
    checkInPreference?: CheckInPreference;
    medias: Media[];
    earlyReturn: boolean;
    allowCheckoutAtArrival?: boolean;
    virtualMeeting?: VirtualMeetingPreference;
    group?: ExperienceGroup;
    downDeposit?: DownDeposit;
    requireGuestIdentification?: boolean;
    catalog: ProductCatalog;
    paymentMethod: PaymentMethod;
    requireCCForSources?: string[];
    waiverPreference?: WaiverExperiencePreference;
    waitlist?: WaitlistPreference;
    balanceDueOffset: number;
    balanceDueReminder: boolean;
    autoChargeOnDepositReminder?: boolean;
    cutoff?: number | null;
    cutoffType?: CutoffType | null;
    alternateCutoff?: number | null;
    futureCutoff?: {
        value: number | null;
        allowOverride?: boolean;
    } | null;
    desc: string;
    excerpt: string;
    cancellation?: {
        policy?: string;
        refundable?: boolean;
    };
    included?: string[];
    notIncluded?: string[];
    other?: string;
    pickupAddress: string;
    emailFooter?: string;
    geo: any;
    meetingLocation?: MeetingLocation | null;
    meetingLocationInstructions?: string;
    attachments?: any[];
    cancellationPolicy?: string;
    terms?: Term;
    reviewPreference?: ReviewPreference;
    travelerPreference?: TravelerPreference;
    photo?: Media;
    guidePreference?: GuidePreference;
    duration: number;
    eventDuration?: number;
    inventory?: {
        allocationStrategy: EquipmentAllocationStrategy;
        prioritizedResources?: PrioritizedResourceID[];
    };
    qrCodePreference?: QrCodePreference;
    firstCheckinExpirationOffset: number | null;
    purchaseExpirationOffset: number | null;
    status?: ExperienceStatus;
    /** @deprecated Replaced by `openingHours`. Safe to remove once back-end drops the field. */
    businessHours?: string;
    openingHours?: OpeningHour[];
    visible?: boolean;
    arrivalOffset?: number | null;
    deletedAt?: Dayjs;
    upsellPreference?: UpsellPreference;
    cancellationProtection?: CancellationProtectionPreference;
    notifications?: ExperienceNotification[];
}

export enum AllowedPrivacy {
    PUBLIC = "public",
    PRIVATE = "private",
}

export interface ExperienceNotification {
    type: NotificationPreferenceType;
    recipients: "organizer" | "travelers";
}

export interface UpsellPreference {
    enabled: boolean;
    hidePrivacyToggle: boolean;
    title: string;
    description: string;
}

export interface DownDeposit {
    id?: ID;
    amount: number;
    amountType: DownDepositAmountType;
    scope: DownDepositScope;
    days: number;
    balanceDueOffset: number;
    type: DownDepositType;
}

export enum DownDepositType {
    REQUIRED = "required",
    OPTIONAL = "optional",
}

export enum DownDepositAmountType {
    PERCENT = "percent",
    ABSOLUTE = "absolute",
}

export enum DownDepositScope {
    OUTING = "outing",
    PERSON = "person",
}

/**
 * OutingMin, outingMax, and outingMinCutoff are only applicable to scheduled experiences,
 * so they are optional in the ExperienceGroup
 */
export interface ExperienceGroup {
    orderMin: number;
    orderMax?: number;
    outingMin?: number;
    outingMax?: number;
    outingMinCutoff?: number;
}

export interface WaitlistPreference {
    id?: ID;
    enabled: boolean;
    enabledForBackOffice: boolean;
    enabledForCheckout: boolean;
    enabledForCappedEvents: boolean;
    checkoutMessage?: string;
    inviteDelay: number;
    notification?: {
        enabled: boolean;
        strategies: string[];
        minOpenSeatPercentage?: number;
    };
    limit?: {
        enabled: boolean;
        max?: number;
    };
}

export enum ScheduleType {
    OPEN = "open",
    DAY = "day",
    TIME = "time",
}

export interface TravelerPreference {
    allowPayment: { value: boolean };
    questionnaire: { value: boolean };
    cancellation: CancellationPreference | null;
    reschedule: ReschedulePreference | null;
}

export type CancellationRefundMethod = PaymentMethod.CREDIT_CARD | PaymentMethod.STORE_CREDIT;

export interface CancellationPreference {
    cutoff: Cutoff;
    refundPercentage: number;
    paymentMethods?: CancellationRefundMethod[];
}

export interface ReschedulePreference {
    cutoff: Cutoff;
    priceChangePolicy?: PriceChangePolicy;
}

export interface Cutoff {
    duration: number;
    strategy: CutoffStrategy;
}

export enum CutoffStrategy {
    CHECK_IN = "check_in",
    ARRIVAL = "arrival",
}

export const CutoffStrategyCopy = {
    [CutoffStrategy.CHECK_IN]: "Check-in",
    [CutoffStrategy.ARRIVAL]: "Scheduled",
};

export enum PriceIncreasePolicy {
    PAYMENT_BEFORE_MODIFICATION = "payment_before_modification",
    PAYMENT_AFTER_MODIFICATION = "payment_after_modification",
}

export enum PriceDecreasePolicy {
    REFUND_DELTA = "refund_delta",
    RETAIN_PRICE = "retain_price",
}

export interface PriceChangePolicy {
    priceIncreasePolicy: PriceIncreasePolicy;
    priceDecreasePolicy: PriceDecreasePolicy;
}

export enum ExperienceStatus {
    DRAFT = "draft",
    PUBLISHED = "published",
    DELETED = "deleted",
}

export enum CutoffType {
    PRE_START = "pre_start",
    PRE_END = "pre_end",
    PRE_OPENING_END = "pre_opening_end",
}

export const CutoffTypeCopy: { [key in string]: string } = {
    [CutoffType.PRE_START]: "start time",
    [CutoffType.PRE_END]: "end time",
    [CutoffType.PRE_OPENING_END]: "end of business",
};

export interface OpeningHoursPerDay {
    day: Weekday;
    timeRange: {
        startTime: number | null; // HHMM int, e.g. 1900 = 7:00 PM
        endTime: number | null;
    };
}

export interface OpeningHour {
    id?: string;
    name?: string | null;
    start: string; // ISO date
    end?: string | null; // ISO date or null for "no end"
    openingHoursPerDay: OpeningHoursPerDay[];
    createdAt?: string;
    updatedAt?: string;
}
